/* eslint-disable max-lines */
/* eslint-disable react/no-unknown-property */
/* eslint-disable @scandipwa/scandipwa-guidelines/only-render-in-component */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import { lazy, PureComponent, Suspense } from 'react';
import { InView } from 'react-intersection-observer';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { DESKTOP_MIN_WIDTH } from 'Component/CategorySort/CategorySort.config';
import ProductCard from 'Component/ProductCard';
import { FilterType } from 'Type/Category.type';
import { ProductType } from 'Type/ProductList.type';
import { HistoryType } from 'Type/Router.type';

import { DEFAULT_PLACEHOLDER_COUNT, DEFAULT_PLACEHOLDER_COUNT_MOBILE } from './ProductListPage.config';

import 'Component/ProductListPage/ProductListPage.style';

export const Html = lazy(() => import(/* webpackMode: "lazy", webpackChunkName: "cms" */ 'Component/Html'));

/** @namespace Scandipwa/Component/ProductListPage/Component/mapStateToProps */
export const mapStateToProps = (state) => ({
    content_blocks: state.CategoryReducer.category.content_blocks
});

/** @namespace Scandipwa/Component/ProductListPage/Component */
export class ProductListPageComponent extends PureComponent {
    static propTypes = {
        isLoading: PropTypes.bool,
        isVisible: PropTypes.bool.isRequired,
        isInfiniteLoaderEnabled: PropTypes.bool.isRequired,
        updatePages: PropTypes.func.isRequired,
        numberOfPlaceholders: PropTypes.number,
        selectedFilters: FilterType,
        pageNumber: PropTypes.number,
        items: PropTypes.arrayOf(ProductType),
        history: HistoryType.isRequired,
        content_blocks: PropTypes.shape({
            top_content: PropTypes.arrayOf(PropTypes.string),
            promo_tile: PropTypes.arrayOf(PropTypes.string)
        })
    };

    static defaultProps = {
        isLoading: false,
        numberOfPlaceholders: DEFAULT_PLACEHOLDER_COUNT,
        selectedFilters: {},
        pageNumber: null,
        items: [],
        content_blocks: {}
    };

    state = {
        isDesktop: false,
        pageViewed: false
    };

    updateWindowDimensions = this.updateWindowDimensions.bind(this);

    handlePageVisibilityToggle = this.handlePageVisibilityToggle.bind(this);

    componentDidMount() {
        this.updateWindowDimensions();
        window.addEventListener('resize', this.updateWindowDimensions);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions);
    }

    updateWindowDimensions() {
        const { isDesktop } = this.state;
        const newIsDesktop = (window.innerWidth >= DESKTOP_MIN_WIDTH);

        if (isDesktop !== newIsDesktop) {
            this.setState({ isDesktop: newIsDesktop });
        }
    }

    handlePageVisibilityToggle(isVisible) {
        const { updatePages, pageNumber } = this.props;
        const { pageViewed } = this.state;

        if (!isVisible) {
            return;
        }

        if (!pageViewed) {
            this.setState({ pageViewed: true }, () => updatePages(pageNumber + 1));
        }
    }

    renderPlaceholders() {
        const { numberOfPlaceholders, isInfiniteLoaderEnabled } = this.props;

        const itemsToRender = (window.innerWidth < DESKTOP_MIN_WIDTH)
            ? DEFAULT_PLACEHOLDER_COUNT_MOBILE : numberOfPlaceholders;

        return Array.from(
            { length: itemsToRender },
            (_, i) => (
                <ProductCard
                  key={ i }
                  product={ {} }
                  isPlaceholder
                  productIndex={ i }
                  isPlp
                  isInfiniteLoaderEnabled={ isInfiniteLoaderEnabled }
                />
            )
        );
    }

    getPlaceholderRef() {
        const { isVisible } = this.props;

        if (!isVisible) {
            return undefined;
        }

        return (node) => {
            this.node = node;
        };
    }

    renderPageItems() {
        const {
            items,
            selectedFilters,
            history,
            isInfiniteLoaderEnabled
        } = this.props;

        return items.map((item) => {
            const { sku = null } = item;

            // Product
            if (sku) {
                return (
                    <ProductCard
                      product={ item }
                      key={ sku }
                      selectedFilters={ selectedFilters }
                      history={ history }
                      isPlp
                      isInfiniteLoaderEnabled={ isInfiniteLoaderEnabled }
                    />
                );
            }

            // Promo tile. item list is merged and prepared in ProductList.resolver
            return (
                <li block="PromoTile">
                    <Suspense fallback={ null }>
                        <Html
                          key={ Math.random().toString() }
                          content={ item }
                        />
                    </Suspense>
                </li>
            );
        });
    }

    renderPlaceholderItems() {
        return (
            <>
                <li
                  block="ProductListPage"
                  elem="Offset"
                  ref={ this.getPlaceholderRef() }
                />
                { this.renderPlaceholders() }
            </>
        );
    }

    renderItems() {
        const { items, isLoading, content_blocks } = this.props;

        const { isDesktop } = this.state;

        if (!items.length || isLoading) {
            return this.renderPlaceholderItems();
        }

        if (isDesktop && Object.keys(content_blocks) === 0) {
            return this.renderPlaceholderItems();
        }

        return this.renderPageItems();
    }

    render() {
        const { pageNumber } = this.props;
        return (
            <InView
              as="ul"
              onChange={ this.handlePageVisibilityToggle }
              className="ProductListPage CategoryProductList-Page"
              key={ pageNumber }
            >
                { this.renderItems() }
            </InView>
        );
    }
}

export default withRouter(connect(mapStateToProps, null)(ProductListPageComponent));
